import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { MAP_COLORS } from '../config/mapConfig'

export interface DraggableMarkerProps {
	map: maplibregl.Map | null
	longitude: number
	latitude: number
	color?: string
	/** Fired once when the user releases the pin, with its new position. */
	onDragEnd: (lngLat: { lng: number; lat: number }) => void
}

/**
 * A single marker the user can pick up and move. Backs the "drop a pin,
 * drag it to the exact spot, capture the address" flow in the evaluation
 * site picker.
 *
 * Position is CONTROLLED: the parent owns the coordinates, and dragging
 * only proposes a new value via `onDragEnd`. That keeps the pin, the form
 * fields, and the reverse-geocode request reading from one source of truth.
 *
 * Deliberately separate from `LocationMarker` rather than a flag on it.
 * LocationMarker recreates its marker whenever any prop changes (its effect
 * depends on the whole `location` object), which would destroy a marker
 * mid-drag and lose the `dragend` event. Here creation and position sync
 * are two effects, so a re-render never tears down a marker the user is
 * holding.
 */
export function DraggableMarker({
	map,
	longitude,
	latitude,
	color = MAP_COLORS.primary,
	onDragEnd,
}: DraggableMarkerProps) {
	const markerRef = useRef<maplibregl.Marker | null>(null)

	// Held in a ref so a new closure from the parent does not re-run the
	// creation effect and drop the marker the user may be dragging.
	const onDragEndRef = useRef(onDragEnd)
	onDragEndRef.current = onDragEnd

	// Only the position at mount time. Later changes go through the second
	// effect, imperatively.
	const initialPositionRef = useRef({ longitude, latitude })

	useEffect(() => {
		if (!map) return

		const el = document.createElement('div')
		// NOTE: do not set `transform` (inline or via hover) on this element —
		// MapLibre uses the marker root's inline `transform` to position it on
		// the map, and any override wipes the translate and snaps the marker
		// to (0,0) of its container. Use box-shadow or filter for hover
		// affordances instead.
		el.style.cssText = `
			width:36px;height:36px;border-radius:50%;
			background:${color};
			display:flex;align-items:center;justify-content:center;
			box-shadow:0 2px 8px rgba(0,0,0,0.35);
			border:2px solid #ffffff;
			cursor:grab;
		`
		const dot = document.createElement('div')
		dot.style.cssText = 'width:10px;height:10px;border-radius:50%;background:#ffffff;'
		el.appendChild(dot)

		const marker = new maplibregl.Marker({ element: el, draggable: true })
			.setLngLat([initialPositionRef.current.longitude, initialPositionRef.current.latitude])
			.addTo(map)

		marker.on('dragstart', () => {
			el.style.cursor = 'grabbing'
		})
		marker.on('dragend', () => {
			el.style.cursor = 'grab'
			const { lng, lat } = marker.getLngLat()
			onDragEndRef.current({ lng, lat })
		})

		markerRef.current = marker

		return () => {
			marker.remove()
			markerRef.current = null
		}
	}, [map, color])

	useEffect(() => {
		if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return
		markerRef.current?.setLngLat([longitude, latitude])
	}, [longitude, latitude])

	return null
}
